/* T653 (Toni 25.10.2026 klo 12.45): «missä / mikä vika» -valitsin — ydinkomponentti. Luettelot tulevat palvelimelta
   (`GET /api/luokitus`, käytössä olevien moduulien luettelot), joten komponentti ei tunne yhtään moduulin koodia. Näyttönimi on
   käännösavain `luok.<koodi>`; tuntematon koodi näkyy koodina eikä kaadu. «Muu» vaatii lisätiedon (palvelin tarkistaa saman). */
import { useEffect, useState } from "react";
import { api } from "./api";
import { t, type KaannosAvain } from "./kieli";
import { KAANNOKSET } from "./kaannokset";
import { Kenttavirhe, type KenttavirheProps } from "./Kenttavirhe";

export interface Luokkavalinta { missa: string; vika: string; lisatieto: string }
export const TYHJA_LUOKKAVALINTA: Luokkavalinta = { missa: "", vika: "", lisatieto: "" };

type Luettelot = Awaited<ReturnType<typeof api.luokitus>>;
const valimuisti = new Map<string, Promise<Luettelot>>();
function haeLuettelot(kohde: string): Promise<Luettelot> {
  let p = valimuisti.get(kohde);
  if (!p) { p = api.luokitus(kohde); valimuisti.set(kohde, p); p.catch(() => valimuisti.delete(kohde)); }
  return p;
}

/** Koodin näyttönimi: `luok.<koodi>`, tuntematon koodi sellaisenaan. */
export function luokkaNimi(koodi: string | null | undefined): string {
  if (!koodi) return "";
  const avain = `luok.${koodi}`;
  return avain in KAANNOKSET ? t(avain as KaannosAvain) : koodi;
}

/** Onko valinta tallennettavissa (sama sääntö kuin palvelimen tarkistaLuokat). */
export function luokkavalintaKunnossa(v: Luokkavalinta, muu = "muu", min = 3): boolean {
  if (!v.missa || !v.vika) return false;
  if ((v.missa === muu || v.vika === muu) && v.lisatieto.trim().length < min) return false;
  return true;
}

type Kentta = { ref?: (el: HTMLSelectElement | null) => void; "aria-invalid"?: boolean; "aria-describedby"?: string };

export function Luokkavalitsin({ kohde, arvo, muuta, ehdotus, naytaLisatieto = true, kentat, virheet }: {
  /** T677 J: kutsujan useKenttavirheet-kentät ja -virheet — puuttuva valinta näytetään punaisena valinnan kohdalla. */
  kentat?: { missa?: Kentta; vika?: Kentta };
  virheet?: { missa?: KenttavirheProps; vika?: KenttavirheProps };
  kohde: string;
  arvo: Luokkavalinta;
  muuta: (v: Luokkavalinta) => void;
  /** Valinnainen esitäyttö (esim. isometrin positiosta johdettu «missä»): näytetään vihjeenä, ei valita itse. */
  ehdotus?: { missa?: string | null; vika?: string | null };
  /** false = kutsujalla on oma kuvaus-/lisätietokenttä (esim. punchin vika-teksti), joka toimii lisätietona. */
  naytaLisatieto?: boolean;
}) {
  const [l, setL] = useState<Luettelot | null>(null);
  useEffect(() => { let elossa = true; void haeLuettelot(kohde).then((x) => { if (elossa) setL(x); }).catch(() => {}); return () => { elossa = false; }; }, [kohde]);
  if (!l) return <p className="dim">{t("luok.ladataan")}</p>;
  const muu = arvo.missa === l.muu || arvo.vika === l.muu;
  return (
    <div data-luokkavalitsin={kohde} style={{ display: "grid", gap: 6, maxWidth: 520 }}>
      <label>{t("luok.missa")}
        <select value={arvo.missa} data-luokka-missa="" style={{ display: "block", width: "100%" }} {...kentat?.missa}
                onChange={(e) => muuta({ ...arvo, missa: e.target.value })}>
          <option value="">{t("luok.valitse")}</option>
          {l.missa.map((k) => <option key={k} value={k}>{luokkaNimi(k)}</option>)}
        </select>
        {ehdotus?.missa && !arvo.missa && (
          <button type="button" className="nappi nappi--kevyt nappi--pieni" data-luokka-ehdotus="missa"
                  onClick={() => muuta({ ...arvo, missa: ehdotus.missa! })}>{t("luok.kayta_ehdotusta", { nimi: luokkaNimi(ehdotus.missa) })}</button>
        )}
        {virheet?.missa && <Kenttavirhe {...virheet.missa} />}
      </label>
      <label>{t("luok.vika")}
        <select value={arvo.vika} data-luokka-vika="" style={{ display: "block", width: "100%" }} {...kentat?.vika}
                onChange={(e) => muuta({ ...arvo, vika: e.target.value })}>
          <option value="">{t("luok.valitse")}</option>
          {/* T653 (Toni 31.10.2026 klo 10.35): «missä»-kohdan omat viat ensin (l.ensin), muut perässä alkuperäisessä järjestyksessä. */}
          {(() => { const e = l.ensin?.[arvo.missa] ?? []; return [...e.filter((k) => l.vika.includes(k)), ...l.vika.filter((k) => !e.includes(k))]; })()
            .map((k) => <option key={k} value={k}>{luokkaNimi(k)}</option>)}
        </select>
        {ehdotus?.vika && !arvo.vika && (
          <button type="button" className="nappi nappi--kevyt nappi--pieni" data-luokka-ehdotus="vika"
                  onClick={() => muuta({ ...arvo, vika: ehdotus.vika! })}>{t("luok.kayta_ehdotusta", { nimi: luokkaNimi(ehdotus.vika) })}</button>
        )}
        {virheet?.vika && <Kenttavirhe {...virheet.vika} />}
      </label>
      {naytaLisatieto ? (
        <label>{muu ? t("luok.lisatieto_pakollinen") : t("luok.lisatieto")}
          <input value={arvo.lisatieto} data-luokka-lisatieto="" maxLength={300} style={{ display: "block", width: "100%" }}
                 onChange={(e) => muuta({ ...arvo, lisatieto: e.target.value })} />
        </label>
      ) : muu ? <p className="dim" style={{ margin: 0 }} data-luokka-muu-ohje="">{t("luok.lisatieto_pakollinen")}</p> : null}
    </div>
  );
}
